/* The counterparty card's Returns tab — the Orders tab's twin (`OrdersTab.js`): the counterparty's
   returns within the window the API sends (`metrics.window_from`), newest first, in the shared
   embedded `DocsTab` (paging, error + retry, first-load skeleton), and the return modal. Its
   section heading (`ReadOnlyTag`, `TabHead`) is `tabParts.js`.

   ⚠ The window is `date_from` alone — `metrics/`'s `window_from`, as the Orders tab, so the list
   waits for it; the API's own `-datetime` default is the "newest first".

   A row opens the same modal `/sales/returns` does (`returns/ReturnModal.js`, same GET) without
   touching the URL. Every link in a row is a real link and stops its own click from reaching the
   row. A visit link — in a row or in the modal — always opens in a new tab, so it never leaves the
   card; every other way out of the card goes through the card's `leaveTo`, which asks before
   unsaved edits are thrown away. The return's page is handed the card's own address
   (`cardLocation`, this tab) as `state.from`, so its Back returns here. */

import React, { useState } from "react";

import { StatusIcon, statusColumn, textOf } from "../../component/table-cells";
import { COUNTERPARTIES_PAGE, RETURNS_PAGE } from "../../config/lang";
import { translate } from "../../util/util";
import ReturnModal from "../returns/ReturnModal";
import { STATUS_ICON } from "../returns/parts";

import DocsTab from "./DocsTab";
import { AuthorCell, NumberCell, PointCell, VisitCell } from "./tabParts";

export default function ReturnsTab({ counterpartyId, lang, cardLocation, leaveTo, windowFrom, windowError, onRetryWindow }) {
  const t = key => translate(COUNTERPARTIES_PAGE, key, lang);
  const [modalId, setModalId] = useState(null);

  const returnPage = id => ({ pathname: `/sales/returns/${id}`, state: { from: cardLocation } });

  /* The Orders tab's columns and cells (`tabParts.js`), on the same content-sized layout
     (`lp-fit`, `scroll.x: "max-content"`). The text columns (number, point, author, visit) carry no
     width: each takes what its content needs and they share the card's spare room in proportion to
     it, so a wide card spreads them evenly. With the author and the visit fixed, all of that room
     went to the point and stood empty between its text and the author. Only the two counts are
     fixed, right-aligned at the right end. Below the content's width the table scrolls inside the
     card, the number pinned. The units column is 120px, not the items' 100: at 100 its header
     started 16px after the items' and the two right-aligned numbers read as one. */
  const columns = [
    /* The icon `/sales/returns` shows — same glyph, tone and tooltip label. */
    statusColumn((v, row) => {
      const { Icon, tone } = STATUS_ICON[row.status] || STATUS_ICON.new;
      const label = translate(RETURNS_PAGE, `return_status_${row.status}`, lang) || row.status;
      return <StatusIcon Icon={Icon} tone={tone} label={label} />;
    }),
    {
      key: "name",
      title: t("o_number_date"),
      fixed: "left",
      render: (v, row) => <NumberCell row={row} to={returnPage(row.id)} leaveTo={leaveTo} />,
    },
    { key: "point", title: t("o_point"), render: (v, row) => <PointCell row={row} leaveTo={leaveTo} /> },
    { key: "created_by", title: t("o_author"), render: (v, row) => <AuthorCell row={row} leaveTo={leaveTo} /> },
    { key: "visit", title: t("o_visit"), render: (v, row) => <VisitCell row={row} lang={lang} /> },
    {
      /* `items_count` / `units_count`: the API's own `Count("items")` / `Sum("items__quantity")`
         annotations. A return carries no money (`ReturnDetail.js`), so no amount column. */
      key: "items_count",
      title: t("r_items"),
      width: 100,
      align: "right",
      render: (v, row) => <span className="lp-num">{textOf(row.items_count)}</span>,
    },
    {
      key: "units_count",
      title: translate(RETURNS_PAGE, "k_units_count", lang),
      width: 120,
      align: "right",
      render: (v, row) => <span className="lp-num">{textOf(row.units_count)}</span>,
    },
  ];

  const navigate = to => leaveTo(null, to);

  return (
    <>
      <DocsTab
        counterpartyId={counterpartyId}
        endpoint="excalibur/returns/"
        detailPath="/sales/returns"
        columns={columns}
        emptyText={t("returns_empty")}
        lang={lang}
        query={windowFrom ? { date_from: windowFrom } : null}
        waitError={windowError}
        onRetryWait={onRetryWindow}
        onRowClick={row => setModalId(row.id)}
        /* Content-sized, as the Orders tab. */
      />
      <ReturnModal
        id={modalId}
        lang={lang}
        onClose={() => setModalId(null)}
        openTo={modalId ? returnPage(modalId) : null}
        navigate={navigate}
        hideCounterparty
      />
    </>
  );
}
